import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { ChevronLeft, Loader2, Save, Sparkles, Play, Pause, Send, Mail, MessageCircle } from 'lucide-react';
import { MarketingLocked } from './LandingPageBuilder';

// Campaign detail — review AI draft, edit any field, then approve/pause/send.
export default function CampaignDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const [saving, setSaving] = useState(false);
  // Local editable copy
  const [form, setForm] = useState({
    subject: '', emailBody: '', smsBody: '',
    sendAt: '', segmentType: 'all', segmentValue: '',
    email: true, sms: true,
  });

  const load = async () => {
    setLoadErr(false);
    try {
      const row = await marketing.getCampaign(cid);
      setC(row);
      setForm({
        subject: row.subject || '',
        emailBody: row.emailBody || '',
        smsBody: row.smsBody || '',
        sendAt: row.sendAt ? toLocalInput(row.sendAt) : '',
        segmentType: row.segment?.type || 'all',
        segmentValue: row.segment?.value || '',
        email: (row.channels || []).includes('email'),
        sms: (row.channels || []).includes('sms'),
      });
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else { setLoadErr(true); toast({ title: 'Load failed' }); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const save = async () => {
    const channels = [form.email && 'email', form.sms && 'sms'].filter(Boolean);
    if (!channels.length) { toast({ title: 'Pick at least one channel' }); return; }
    setSaving(true);
    try {
      const patch = {
        subject: form.subject,
        emailBody: form.emailBody,
        smsBody: form.smsBody,
        sendAt: form.sendAt ? fromLocalInput(form.sendAt) : null,
        segment: { type: form.segmentType, value: form.segmentValue || null },
        channels,
      };
      const r = await marketing.patchCampaign(cid, patch);
      setC(r);
      toast({ title: 'Saved' });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    await save();
    try {
      const r = await marketing.approveCampaign(cid);
      setC(r);
      toast({ title: 'Approved — cron will send at scheduled time' });
    } catch (e) { toast({ title: 'Approve failed', description: e?.response?.data?.detail || '' }); }
  };
  const pause = async () => {
    try { await marketing.pauseCampaign(cid); load(); toast({ title: 'Paused' }); }
    catch (e) { toast({ title: 'Pause failed' }); }
  };
  const sendNow = async () => {
    if (!window.confirm('Send this campaign to everyone in the segment right now?')) return;
    await save();
    try {
      const r = await marketing.sendCampaignNow(cid);
      toast({ title: 'Dispatched', description: `queued=${r.queued} email=${r.emailSent} sms=${r.smsSent}` });
      load();
    } catch (e) { toast({ title: 'Send failed', description: e?.response?.data?.detail || '' }); }
  };

  if (locked) return <MarketingLocked />;
  if (loadErr && !c) return (
    <div className="max-w-lg space-y-3" data-testid="campaign-detail-load-error">
      <Link to="/marketing/campaigns" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1">
        <ChevronLeft size={14}/> Back to campaigns
      </Link>
      <div className="bg-gray-900 border border-red-900 rounded-lg p-4 text-white">
        <div className="font-semibold text-red-300">Couldn't load this campaign</div>
        <div className="text-xs text-gray-400 mt-1">Network hiccup or the campaign may have been deleted.</div>
        <button data-testid="campaign-detail-retry" onClick={load}
                className="mt-3 pbb-btn-green inline-flex items-center gap-1 text-sm">
          Retry
        </button>
      </div>
    </div>
  );
  if (!c) return <div className="text-gray-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;

  const readOnly = c.status === 'sending' || c.status === 'sent';

  return (
    <div className="space-y-4 max-w-4xl" data-testid="campaign-detail-page">
      <Link to="/marketing/campaigns" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1">
        <ChevronLeft size={14}/> Back to campaigns
      </Link>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs text-gray-500">Campaign · {c.status.toUpperCase()}</div>
          <h1 className="text-2xl font-bold text-white">{c.subject || '(no subject)'}</h1>
          {c.aiGenerated && <div className="text-xs text-green-400 mt-1 inline-flex items-center gap-1"><Sparkles size={12}/> AI-drafted from your business profile</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          {!readOnly && (
            <button data-testid="campaign-detail-save" onClick={save} disabled={saving}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800 disabled:opacity-60">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save
            </button>
          )}
          {c.status === 'draft' && (
            <button data-testid="campaign-detail-approve" onClick={approve}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded bg-green-600 hover:bg-green-500 text-white">
              <Play size={12}/> Approve to schedule
            </button>
          )}
          {c.status === 'scheduled' && (
            <button data-testid="campaign-detail-pause" onClick={pause}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
              <Pause size={12}/> Pause
            </button>
          )}
          {(c.status === 'draft' || c.status === 'scheduled' || c.status === 'paused') && (
            <button data-testid="campaign-detail-send-now" onClick={sendNow}
                    className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-gray-800">
              <Send size={12}/> Send now
            </button>
          )}
        </div>
      </div>

      {/* Live stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Queued" value={c.stats?.queued || 0}/>
        <Stat label="Email sent" value={c.stats?.emailSent || 0}/>
        <Stat label="Email delivered" value={c.stats?.emailDelivered || 0}/>
        <Stat label="Email opened" value={c.stats?.emailOpened || 0}/>
        <Stat label="SMS sent" value={c.stats?.smsSent || 0}/>
        <Stat label="SMS delivered" value={c.stats?.smsDelivered || 0}/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
          <label className="block text-xs text-gray-400 mb-1">Send at</label>
          <input data-testid="campaign-form-sendAt" type="datetime-local" disabled={readOnly}
                 value={form.sendAt} onChange={e => setForm({ ...form, sendAt: e.target.value })}
                 className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
          <label className="block text-xs text-gray-400 mb-1">Segment</label>
          <div className="flex gap-2">
            <select data-testid="campaign-form-segment-type" disabled={readOnly}
                    value={form.segmentType} onChange={e => setForm({ ...form, segmentType: e.target.value })}
                    className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-white text-sm">
              <option value="all">All contacts</option>
              <option value="tag">By tag</option>
              <option value="city">By city</option>
            </select>
            {form.segmentType !== 'all' && (
              <input data-testid="campaign-form-segment-value" disabled={readOnly}
                     value={form.segmentValue} onChange={e => setForm({ ...form, segmentValue: e.target.value })}
                     placeholder={form.segmentType === 'tag' ? 'tag name' : 'city name'}
                     className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
            )}
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
          <label className="block text-xs text-gray-400 mb-2">Channels</label>
          <div className="flex gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
              <input data-testid="campaign-form-email-chk" type="checkbox" disabled={readOnly}
                     checked={form.email} onChange={e => setForm({ ...form, email: e.target.checked })}/>
              <Mail size={14}/> Email
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-white cursor-pointer">
              <input data-testid="campaign-form-sms-chk" type="checkbox" disabled={readOnly}
                     checked={form.sms} onChange={e => setForm({ ...form, sms: e.target.checked })}/>
              <MessageCircle size={14}/> SMS
            </label>
          </div>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
        <label className="block text-xs text-gray-400 mb-1">Email subject</label>
        <input data-testid="campaign-form-subject" disabled={readOnly}
               value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
               className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
        <label className="block text-xs text-gray-400 mb-1">Email body (HTML allowed)</label>
        <textarea data-testid="campaign-form-emailBody" rows={10} disabled={readOnly}
                  value={form.emailBody} onChange={e => setForm({ ...form, emailBody: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm font-mono"/>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
        <label className="block text-xs text-gray-400 mb-1">SMS body <span className="text-gray-500">({form.smsBody.length}/240 chars)</span></label>
        <textarea data-testid="campaign-form-smsBody" rows={3} disabled={readOnly} maxLength={240}
                  value={form.smsBody} onChange={e => setForm({ ...form, smsBody: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm"/>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-white text-xl font-bold">{value}</div>
    </div>
  );
}

// Convert ISO to <input type="datetime-local"> value (YYYY-MM-DDTHH:MM local).
function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}
function fromLocalInput(v) {
  try { return new Date(v).toISOString().replace('Z', ''); } catch { return v; }
}
